import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SolutionDialog } from "../components/SolutionDialog";
import { useLang } from "../i18n/LanguageContext";
import { solutionCategories } from "../content/solutions";

export default function SolutionsPage() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const filtered = filter === "all" ? solutionCategories : solutionCategories.filter((s) => s.id === filter);

  return (
    <>
      <SEO page="solutions" />
      <PageHero eyebrow={t.solutions.eyebrow} title={t.solutions.pageTitle} sub={t.solutions.pageSub} testId="solutions-hero" />

      <section className="bg-[#F7F8F5] py-20 md:py-28">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-wrap gap-2.5 mb-6" role="group" aria-label={t.a11y.filters} data-testid="solutions-filters">
              <button
                onClick={() => setFilter("all")}
                data-testid="filter-all"
                aria-pressed={filter === "all"}
                className={`px-5 py-2 text-xs font-semibold tracking-widest uppercase border transition-colors duration-300 ${
                  filter === "all" ? "bg-[#061A26] text-white border-[#061A26]" : "border-black/15 text-slate-600 hover:border-[#19C7C1] hover:text-[#061A26]"
                }`}
              >
                {t.solutions.filterAll}
              </button>
              {solutionCategories.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setFilter(s.id)}
                  data-testid={`filter-${s.id}`}
                  aria-pressed={filter === s.id}
                  className={`px-5 py-2 text-xs font-semibold tracking-widest uppercase border transition-colors duration-300 ${
                    filter === s.id ? "bg-[#061A26] text-white border-[#061A26]" : "border-black/15 text-slate-600 hover:border-[#19C7C1] hover:text-[#061A26]"
                  }`}
                >
                  {t.solutionsData[s.id].name}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 max-w-2xl mb-12" data-testid="solutions-demo-note">{t.solutions.demoNote}</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="solutions-grid">
            {filtered.map((s, i) => {
              const data = t.solutionsData[s.id];
              return (
              <Reveal key={s.id} delay={(i % 3) * 0.08} y={20}>
                <button
                  onClick={() => setActive(s)}
                  data-testid={`solutions-page-card-${s.id}`}
                  className="group w-full text-left bg-white border border-black/5 overflow-hidden hover:border-[#19C7C1]/40 hover:-translate-y-1 transition-[transform,border-color] duration-500"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={s.image} alt={data.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    <span className="absolute top-4 left-4 text-[10px] tracking-[0.15em] uppercase bg-[#061A26]/70 backdrop-blur-sm text-white/90 px-3 py-1.5">
                      {t.solutions.demoBadge}
                    </span>
                  </div>
                  <div className="p-7">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-base md:text-lg font-semibold text-[#061A26]">{data.name}</h3>
                      <ArrowUpRight size={18} className="shrink-0 mt-1 text-slate-400 group-hover:text-[#19C7C1] transition-colors duration-300" />
                    </div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{data.desc}</p>
                    <div className="mt-5 pt-5 border-t border-black/5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
                      <span><strong className="text-slate-700 font-medium">{t.solutions.availabilityLabel}:</strong> {data.availability}</span>
                    </div>
                  </div>
                </button>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <SolutionDialog solution={active} onClose={() => setActive(null)} />
    </>
  );
}
